import { ReactNode, useState } from 'react';
import { Link } from 'react-router-dom';
import { ActionIcon, Collapse, Flex, Text } from '@mantine/core';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import { InvestorDetailsCardStyles } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/InvestorDetailsCard/InvestorDetailsCard.styles.tsx';
import type { InvestorDetailsCardAction } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/InvestorDetailsCard/InvestorDetailsCard.types.ts';

interface Props {
	title: string;
	/** Shown beside the title only while collapsed, as a summary of the hidden rows. */
	count?: number;
	action?: InvestorDetailsCardAction;
	children: ReactNode;
	dataTestId?: string;
}

export const InvestorDetailsCard = ({ title, count, action, children, dataTestId }: Props) => {
	const isExpandable = action?.kind === 'expand';
	const [expanded, setExpanded] = useState(
		action?.kind === 'expand' ? action.defaultExpanded !== false : true
	);

	return (
		<InvestorDetailsCardStyles data-testid={dataTestId}>
			<div className="header">
				<Flex align="center" gap="0.25rem">
					<Text fz={14} fw={600} c="dark.8">
						{title}
					</Text>

					{count !== undefined && !expanded && (
						<Text fz="sm" c="dark.4" className="count">
							{count}
						</Text>
					)}
				</Flex>

				{action?.kind === 'link' && (
					<Link to={action.to} aria-label={title}>
						<ActionIcon variant="subtle" size="sm" c="dark.8">
							<Icon variant="arrow-up-right-2" size="md" />
						</ActionIcon>
					</Link>
				)}

				{action?.kind === 'edit' && (
					<ActionIcon
						variant="subtle"
						size="sm"
						c="dark.8"
						aria-label={title}
						onClick={action.onEdit}
					>
						<Icon variant="pencil-edit-line" size="lg" />
					</ActionIcon>
				)}

				{isExpandable && (
					<ActionIcon
						variant="subtle"
						size="sm"
						c="dark.8"
						aria-label={title}
						aria-expanded={expanded}
						onClick={() => setExpanded((current) => !current)}
					>
						<Icon variant={expanded ? 'caret-up' : 'caret-down'} size="md" />
					</ActionIcon>
				)}
			</div>

			{isExpandable ? (
				<Collapse in={expanded} className="content">
					<div className="content-inner">{children}</div>
				</Collapse>
			) : (
				<div className="content">
					<div className="content-inner">{children}</div>
				</div>
			)}
		</InvestorDetailsCardStyles>
	);
};
